import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Text,
  InputGroup,
  useToast,
  FormControl,
  Switch,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ImageUpload } from '@/components/ImageUpload';
import { formatDateToDateTimeLocalInputValue } from '@/utils/formatDateToDateTimeLocalInputValue';
import { usePutFundraisingCampaignMutation } from '@/services/queries/fundraising-campaign';
import { FundraisingCampaign, FundraisingCampaignStatus } from '@/model/fundraising-campaign';
import { UpdateFundraisingCampaignPayload } from '@/services/request/fundraising-campaign';
import { TextareaField } from '@/components/TextareaField';
import { useTranslation } from 'react-i18next';

export interface FundraisingCampaignEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: FundraisingCampaign;
  onClose: () => void;
  onDone?: () => void;
}

export const FundraisingCampaignEditModal = ({ isOpen, onClose, onDone, item }: FundraisingCampaignEditModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const updateFundraisingCampaignMutation = usePutFundraisingCampaignMutation();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
    clearErrors,
    reset,
    watch,
  } = useForm<ModalFormData>({
    defaultValues: {
      title: item.title,
      description: item.description,
      goal: item.goal / 100,
      pix_key: item.pix_key,
      image: item.image,
      start_date: item.start_at,
      end_date: item.end_at,
      status: item.status,
    },
    resolver: zodResolver(modalFormSchema),
  });

  console.log({ errors });

  const formValues = watch();

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload: UpdateFundraisingCampaignPayload = {
        title: data.title,
        description: data.description,
        goal: data.goal,
        pix_key: data.pix_key,
        image: data.image,
        start_at: data.start_date,
        end_at: data.end_date,
        status: data.status as FundraisingCampaignStatus,
      };

      updateFundraisingCampaignMutation.mutate(
        { id: item.id, payload },
        {
          onSuccess: () => {
            onDone?.();
            reset();
            toast({
              status: 'success',
              title: t(
                'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_success_toast_title',
              ),
            });
          },
          onError: () => {
            toast({
              status: 'error',
              title: t(
                'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_error_toast_title',
              ),
            });
          },
        },
      );
    },
    [item.id, onDone, reset, toast, updateFundraisingCampaignMutation, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_modal_title')}
            </Text>

            {item?.status !== FundraisingCampaignStatus.ENDED ? (
              <FormControl w='fit-content' display='flex' gap={2} alignItems='center'>
                <Text fontSize='14px' fontWeight='medium'>
                  {t(
                    'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_published_label',
                  )}
                </Text>
                <Switch
                  name='is_scheduled'
                  isChecked={formValues.status === FundraisingCampaignStatus.PUBLISHED}
                  onChange={event => {
                    setValue(
                      'status',
                      event.target.checked ? FundraisingCampaignStatus.PUBLISHED : FundraisingCampaignStatus.NOT_PUBLISHED,
                    );
                  }}
                  size='sm'
                />
              </FormControl>
            ) : null}
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <InputField
              withoutFloating
              label={t(
                'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_title_label',
              )}
              {...register('title')}
              helperText={t(
                'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_title_helper_text',
              )}
              maxLength={100}
              rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
            />

            <TextareaField
              label={t(
                'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_description_label',
              )}
              {...register('description')}
              helperText={t(
                'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_description_helper_text',
              )}
              maxLength={4000}
              error={errors?.description?.message}
            />

            <InputField
              withoutFloating
              label={t(
                'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_goal_label',
              )}
              {...register('goal', { setValueAs: value => value * 100 })}
              placeholder={t(
                'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_goal_placeholder',
              )}
              rootProps={{ isInvalid: !!errors?.goal, errorMessage: errors?.goal?.message }}
            />

            <InputField
              withoutFloating
              label={t(
                'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_pix_key_label',
              )}
              {...register('pix_key')}
              rootProps={{ isInvalid: !!errors?.pix_key, errorMessage: errors?.pix_key?.message }}
            />

            <InputGroup display='flex' flexDirection='column' width='100%' mt={4}>
              <ImageUpload
                label={t(
                  'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_image_label',
                )}
                imageURL={''}
                onFileChange={file => setValue('image', file)}
                helpText={t(
                  'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_image_help_text',
                )}
                error={errors?.image?.message?.toString()}
              />
            </InputGroup>

            <FormControl display='flex' gap={2} alignItems='center' mt={4}>
              <Text fontWeight='medium'>
                {t(
                  'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_schedule_label',
                )}
              </Text>
              <Switch
                name='is_scheduled'
                isChecked={formValues.status === FundraisingCampaignStatus.SCHEDULED}
                onChange={event => {
                  setValue(
                    'status',
                    event.target.checked ? FundraisingCampaignStatus.SCHEDULED : FundraisingCampaignStatus.NOT_PUBLISHED,
                  );
                }}
              />
            </FormControl>

            {formValues.status === FundraisingCampaignStatus.SCHEDULED ? (
              <InputField
                withoutFloating
                label={t(
                  'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_start_date_label',
                )}
                type='datetime-local'
                fullDatePickerArea
                {...register('start_date')}
                rootProps={{ isInvalid: !!errors?.start_date, errorMessage: errors?.start_date?.message }}
              />
            ) : null}

            {formValues.status === FundraisingCampaignStatus.SCHEDULED ? (
              <InputField
                withoutFloating
                label={t(
                  'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_end_date_label',
                )}
                type='datetime-local'
                fullDatePickerArea
                min={formatDateToDateTimeLocalInputValue(new Date())}
                {...register('end_date')}
                rootProps={{ isInvalid: !!errors?.end_date, errorMessage: errors?.end_date?.message }}
              />
            ) : null}
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t(
                  'pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_cancel_button',
                )}
              </Button>
              <Button onClick={handleConfirm} paddingX={10}>
                {t('pages.fundraising_campaign.components.fundraising_campaign_edit_modal.fundraising_campaign_edit_save_button')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>

      <CenteredSpinner isLoading={updateFundraisingCampaignMutation.isLoading} />
    </Modal>
  );
};
